import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { Space, Form, Button, Select } from 'antd';
import { EditOutlined, DeleteOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import {
  Table,
  ApplicationUIContext,
  Drawer,
  DrawerContent,
  useDrawerState,
} from '@yuhuinc/ui-library';
import { getTranslatedString } from 'helpers/i18n';
import { applicantTypeRadios } from '../assign_additional_applicant';
import NewOrEditApplicant from './NewOrEditApplicant';
import NewApplicantErrorAlert from './NewApplicantError';
import { buildInitialValuesForSelectedApplicant } from './helpers';
import { ContactMethod } from '../additional_information/ContactMethod';

const StyledTable = styled(Table)`
  margin-bottom: 16px;
`;

const AdditionalApplicantsTableRowActions = ({
  siteAndProperties,
  applicant,
  onUpdate,
  onDelete,
}) => {
  const [, editAdditionalApplicantDrawer] = useDrawerState(false);
  const [editApplicantError, setEditApplicantError] = useState(null);
  const { isMobile } = useContext(ApplicationUIContext);

  function onEditApplicant(values) {
    onUpdate(values);
    editAdditionalApplicantDrawer.close();
  }

  return (
    <>
      <Space size="middle">
        <Button type="link" icon={<EditOutlined />} onClick={editAdditionalApplicantDrawer.open} />
        <Button type="link" icon={<DeleteOutlined />} onClick={onDelete} />
      </Space>
      <Drawer
        title={getTranslatedString(
          'landlordPortal.applicationManagement.inviteLeadToApply.additionalApplicantUpdateDrawerTitle'
        )}
        visible={editAdditionalApplicantDrawer.isVisible}
        closable
        destroyOnClose
        onClose={editAdditionalApplicantDrawer.close}
      >
        <DrawerContent>
          {editApplicantError && (
            <NewApplicantErrorAlert {...editApplicantError} isMobile={isMobile} />
          )}
          <NewOrEditApplicant
            initialValues={buildInitialValuesForSelectedApplicant(applicant)}
            extraFormItems={
              <>
                <ContactMethod />
                {applicantTypeRadios}
              </>
            }
            onSubmit={onEditApplicant}
            onCancel={editAdditionalApplicantDrawer.close}
            onError={setEditApplicantError}
          />
        </DrawerContent>
      </Drawer>
    </>
  );
};

const AdditionalApplicantsTable = ({
  siteAndProperties,
  applicants,
  operations,
  applicationTemplates,
}) => {
  const { isMobile } = useContext(ApplicationUIContext);
  const form = Form.useFormInstance();

  const columns = [
    {
      title: getTranslatedString(
        'landlordPortal.applicationManagement.inviteLeadToApply.addAdditionalApplicant.applicantName'
      ),
      dataIndex: 'full_name',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              return getFieldValue(['additional_applicants', row.name, 'full_name']);
            }}
          </Form.Item>
        );
      },
    },
    {
      title: getTranslatedString(
        'landlordPortal.applicationManagement.inviteLeadToApply.addAdditionalApplicant.email'
      ),
      dataIndex: 'email',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              return getFieldValue(['additional_applicants', row.name, 'email']);
            }}
          </Form.Item>
        );
      },
    },
    {
      title: getTranslatedString(
        'landlordPortal.applicationManagement.inviteLeadToApply.addAdditionalApplicant.applicantType'
      ),
      dataIndex: 'applicant_type',
      render(_, row) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue }) => {
              const value = getFieldValue(['additional_applicants', row.name, 'applicant_type']);
              return getTranslatedString(
                `landlordPortal.applicationManagement.inviteLeadToApply.addAdditionalApplicant.applicantType.${value}`
              );
            }}
          </Form.Item>
        );
      },
    },
    {
      key: 'actions',
      render(_, row, index) {
        return (
          <Form.Item shouldUpdate>
            {({ getFieldValue, setFields }) => {
              return (
                <AdditionalApplicantsTableRowActions
                  applicant={getFieldValue(['additional_applicants', row.name])}
                  siteAndProperties={siteAndProperties}
                  onUpdate={(value) =>
                    setFields([{ name: ['additional_applicants', row.name], value }])
                  }
                  onDelete={() => operations.remove(row.name)}
                />
              );
            }}
          </Form.Item>
        );
      },
    },
  ];

  return (
    <StyledTable
      dataSource={applicants}
      columns={columns}
      hideActionAndSearch
      size={isMobile ? 'middle' : 'small'}
      pagination={false}
      rowKey={(record) => {
        return (
          form.getFieldValue(['additional_applicants', record.name, 'id']) ||
          form.getFieldValue(['additional_applicants', record.name, 'email'])
        );
      }}
      scroll={{
        x: isMobile,
      }}
    />
  );
};

AdditionalApplicantsTable.propTypes = {
  siteAndProperties: PropTypes.array,
  applicants: PropTypes.array,
  onEdit: PropTypes.func,
  onRemove: PropTypes.func,
  operations: PropTypes.object,
};

export default AdditionalApplicantsTable;
